import {
	PRODUCT_TYPE_BUNDLE,
	PRODUCT_TYPE_CONFIGURABLE,
	PRODUCT_TYPE_GIFTCARD,
	PRODUCT_TYPE_GROUPED,
	OPTIONS_KEY_BUNDLE,
	OPTIONS_KEY_CONFIGURABLE,
	OPTIONS_KEY_GIFTCARD,
	OPTIONS_KEY_GROUPED,
	OPTIONS_KEY_CUSTOM,
} from '../constants'

export const productImageByType = (assets, role = 'image', type = 'image') => {
	return assets.find((asset) => asset.type === type && asset.roles.includes(role))
}

export const productMainImage = productImageByType

// Inspired by https://stackoverflow.com/a/43055893/2069172
export function optionsCombinations(options) {
	return options
		? (function recurse(keys) {
				if (!keys.length) return [{}]
				let result = recurse(keys.slice(1))
				return options[keys[0]].values.reduce(
					(acc, { value }) =>
						acc.concat(
							result.map((item) =>
								Object.assign({}, item, {
									[keys[0]]: { id: options[keys[0]].attributeId, value },
								})
							)
						),
					[]
				)
		  })(Object.keys(options)).map(Object.values)
		: []
}

export const getMetadataOptions = (productType, item) => {
	const { metadata } = item

	switch (productType) {
		case PRODUCT_TYPE_CONFIGURABLE:
			return metadata.configurableOptions
		case PRODUCT_TYPE_BUNDLE:
			return metadata.bundleOptions
		case PRODUCT_TYPE_GIFTCARD:
			return metadata.giftcardOptions
		case PRODUCT_TYPE_GROUPED:
			return metadata.groupedOptions
		default:
			return null
	}
}

const configurableOptionsToFormDefaultValues = (configurableOptions) =>
	configurableOptions && configurableOptions?.length > 0
		? configurableOptions?.reduce(
				(acc, { id, value }) => ({
					...acc,
					[`_${id}`]: value,
				}),
				{}
		  )
		: undefined

const customOptionsToFormDefaultValues = (customOptions) =>
	customOptions && customOptions?.length > 0
		? customOptions?.reduce(
				(acc, { id, value }) => ({ ...acc, [isNaN(id) ? id : `_${id}`]: isNaN(value) ? value : `_${value}` }),
				{}
		  )
		: null
/**
 * Transform metadata to form default values for react-hook-form on product provider
 */
export const itemMetadataToFormDefaultValues = (item) => {
	const {
		metadata = {},
		productData: { bundleOptions, type },
	} = item

	const formDefaultValues = {}

	switch (type) {
		case PRODUCT_TYPE_CONFIGURABLE:
			formDefaultValues[OPTIONS_KEY_CONFIGURABLE] = configurableOptionsToFormDefaultValues(
				metadata?.configurableOptions
			)
			break
		case PRODUCT_TYPE_BUNDLE:
			formDefaultValues[OPTIONS_KEY_BUNDLE] =
				metadata?.bundleOptions && metadata?.bundleOptions?.length > 0
					? metadata?.bundleOptions.reduce((acc, { id, selections }) => {
							const bundleOption = bundleOptions?.items?.find((item) => item.id === id)
							if (!bundleOption) return acc

							const { type } = bundleOption
							let selectionsValue
							let qtyValue
							switch (type) {
								case 'select':
								case 'radio':
									selectionsValue = selections[0].id.toString()
									qtyValue = selections[0].qty
									break

								case 'checkbox':
								case 'multi':
									selectionsValue = selections.reduce((acc, { id }) => ({ ...acc, [`_${id}`]: true }), {})
									qtyValue = selections.reduce((acc, { id, qty }) => ({ ...acc, [`_${id}`]: qty }), {})
							}

							return { ...acc, [`_${id}`]: { selections: selectionsValue, qty: qtyValue } }
					  }, {})
					: null
			break
		case PRODUCT_TYPE_GIFTCARD:
			formDefaultValues[OPTIONS_KEY_GIFTCARD] = metadata?.giftcardOptions ?? null
			break
		case PRODUCT_TYPE_GROUPED:
			formDefaultValues[OPTIONS_KEY_GROUPED] =
				metadata.groupedOptions && metadata.groupedOptions.length > 0
					? metadata.groupedOptions.reduce((acc, { sku }) => ({ ...acc, [`_${sku}`]: true }), {})
					: []
			break
	}

	formDefaultValues[OPTIONS_KEY_CUSTOM] = customOptionsToFormDefaultValues(metadata?.customOptions)

	return formDefaultValues
}

export const itemMetadataToChildrenFormDefaultValues = (item) => {
	const {
		metadata,
		productData: { type },
	} = item

	switch (type) {
		case PRODUCT_TYPE_GROUPED:
			return metadata?.groupedOptions
				.filter(({ configurableOptions }) => !!configurableOptions)
				.map(({ configurableOptions, ...groupedOption }) => ({
					...groupedOption,
					formDefaultValues: {
						[OPTIONS_KEY_CONFIGURABLE]: configurableOptionsToFormDefaultValues(configurableOptions),
					},
				}))
		default:
			return null
	}
}

export const getAttributesByOptionsSelected = (options = [], selectedOptions = []) => {
	return Array.isArray(selectedOptions)
		? selectedOptions
				.map(({ id, value: valueId }) => {
					const option = options.find((option) => option.attributeId === id)
					const optionValueLabel = option?.values?.find(({ value }) => value === valueId)?.label
					return option ? { label: option.label, value: optionValueLabel } : null
				})
				.filter((option) => !!option)
		: []
}
